import React, { useMemo, useState } from 'react';
import ScopeThreeHeader from 'shared/components/Scope3Header';
import styles from './categoryOne.module.scss';
import { ReactComponent as CategoryOne } from 'assets/icons/box_cat1.svg';
import SharedAccordion from 'shared/components/Accordion';
import useFilters from 'shared/hooks/useFilters';
import { queryParamsList } from 'views/ActivityDataEntry';
import { useYearLabel } from 'shared/utils/generateYearSpan';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  FieldErrors,
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import {
  ScopeThreeCategoryOne,
  ScopeThreeSection,
} from 'models/ScopeThreeCategoryOne/ScopeThreeCategoryOne.model';
import { Category1TypeFields, Category1Types } from 'enums/category1Enums';
import { PanelComponent } from './panelComponent';
import ProductsService from 'services/ProductsService/products.service';
import { useEffect } from 'react';
import ScopeThreeService from 'services/ScopeThreeService/scopeThree.service';
import { useParams } from 'react-router-dom';
import { validationSchema } from './validationSchema';
import useRedirect from 'shared/utils/useRedirect';
import { ActivityDataEntryScopeTabs } from 'enums/ScopeThreeCategoryStatusEnums';
import { Spin } from 'antd';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/NotificationEnums';
import Stage1AndStage2ProductService from 'services/ProductsService/stage1AndStage2Product.service';

const CategoryOneComponent = () => {
  const { redirectToActivityDataEntry } = useRedirect();
  const {
    fetchIngredients,
    rawMaterialIngredientsMeta,
    fetchPackagingMaterials,
    rawMateialPackagingMeta,
  } = Stage1AndStage2ProductService();
  const {
    loading,
    fetchCategoryOne,
    updateCategoryOne,
    fetchOtherMeta,
    othersMeta,
  } = ScopeThreeService();
  const { id: locationId } = useParams();
  const { getParams } = useFilters();
  const { activeTab } = getParams(queryParamsList);
  const [activeTabKey, setActiveTabKey] = useState<string | string[]>('1');

  useEffect(() => {
    fetchIngredients();
    fetchPackagingMaterials();
    fetchOtherMeta();
  }, []);

  const fetchCategoryOneData = async () => {
    const initialValues: ScopeThreeCategoryOne = await fetchCategoryOne(
      Number(locationId),
      activeTab
    );
    const areAllArraysEmpty =
      !initialValues?.ingredients?.[0]?.activityLabel &&
      !initialValues?.packagings?.[0]?.activityLabel &&
      !initialValues?.others?.[0]?.activityLabel;
    return !areAllArraysEmpty
      ? {
          ...initialValues,
          ingredients: initialValues?.ingredients?.length
            ? initialValues?.ingredients
            : [new ScopeThreeSection()],
          packagings: initialValues?.packagings?.length
            ? initialValues?.packagings
            : [new ScopeThreeSection()],
          others: initialValues?.others?.length
            ? initialValues?.others
            : [new ScopeThreeSection()],
        }
      : new ScopeThreeCategoryOne();
  };

  const methods = useForm<ScopeThreeCategoryOne>({
    defaultValues: fetchCategoryOneData,
    resolver: yupResolver(validationSchema),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: Category1TypeFields.INGREDIENTS, // Name of the array in the form data
  });

  const {
    fields: packagingMaterialFields,
    append: appendPackagingMaterial,
    remove: removePackagingMaterial,
  } = useFieldArray({
    control: methods.control,
    name: Category1TypeFields.PACKAGING_MATERIALS,
  });

  const {
    fields: otherMaterialFields,
    append: appendOtherMaterial,
    remove: removeOtherMaterial,
  } = useFieldArray({
    control: methods.control,
    name: Category1TypeFields.OTHERS,
  });
  const handleAccordionChange = (key: string | string[]) =>
    setActiveTabKey(key);

  // Helper function for setting errors
  const setOriginShareError = (
    field: Category1TypeFields,
    index: number,
    originIndex: number
  ) => {
    methods?.setError(`${field}.${index}.origins.${originIndex}.share`, {
      message: 'Sum of shares must be 100',
    });
  };

  // Validate sum of shares across different categories
  const validateOriginsShareSum = (
    items: ScopeThreeSection[],
    field: Category1TypeFields
  ) => {
    let isValid = true;

    items?.forEach((item, index) => {
      if (item?.origins?.length > 1 && item?.activityLabel) {
        const totalShare = item.origins.reduce(
          (sum, origin) => sum + (origin?.share || 0),
          0
        );

        if (totalShare !== 100) {
          isValid = false;
          item.origins.forEach((_, originIndex) => {
            setOriginShareError(field, index, originIndex);
          });
        }
      }
    });

    return isValid;
  };

  // Custom validation function
  const customValidate = (data: ScopeThreeCategoryOne) => {
    const categories = [
      { key: 'ingredients', field: Category1TypeFields.INGREDIENTS },
      { key: 'packagings', field: Category1TypeFields.PACKAGING_MATERIALS },
      { key: 'others', field: Category1TypeFields.OTHERS },
    ];

    return categories.every(({ key, field }) => {
      const items = data?.[key as keyof ScopeThreeCategoryOne];
      return items ? validateOriginsShareSum(items, field) : true;
    });
  };

  const filterItems = (items?: Array<ScopeThreeSection>) =>
    items?.reduce((acc, item) => {
      if (!!item.activityLabel) {
        if (item?.origins?.length === 1) {
          acc.push({ ...item, origins: [{ ...item?.origins[0], share: 100 }] });
        } else {
          acc.push(item);
        }
      }
      return acc;
    }, [] as Array<ScopeThreeSection>);

  const filterData = (data: ScopeThreeCategoryOne) => ({
    ...data,
    ingredients: filterItems(data?.ingredients),
    packagings: filterItems(data?.packagings),
    others: filterItems(data?.others),
  });

  // Handle form submission
  const onSubmit: SubmitHandler<ScopeThreeCategoryOne> = async data => {
    const modifiedData = filterData(data);

    // Check if any category has at least one valid activityLabel
    const hasValidEntries = Object.values(modifiedData).some(
      category => category?.length && category[0]?.activityLabel
    );

    if (!hasValidEntries) {
      Notification({
        message: 'Please fill at least 1 entry to submit',
        description: '',
        type: NotificationTypes.ERROR,
      });
      return;
    }

    // Run custom validation
    if (!customValidate(modifiedData)) return;

    // Update data and handle redirect
    const response = await updateCategoryOne(
      Number(locationId),
      activeTab,
      modifiedData
    );
    if (response) {
      redirectToActivityDataEntry(
        locationId,
        {
          activeTab,
          scopeTab: ActivityDataEntryScopeTabs.SCOPE_3_UPSTREAM,
        },
        true
      );
    }
  };

  const checkForError = (errorObj: FieldErrors<ScopeThreeCategoryOne>) => {
    let errorCategoryList: Category1Types[] = [];
    if (!errorObj) return [];

    if (errorObj.hasOwnProperty(Category1TypeFields.INGREDIENTS)) {
      errorCategoryList.push(Category1Types.INGREDIENTS);
    }
    if (errorObj.hasOwnProperty(Category1TypeFields.PACKAGING_MATERIALS)) {
      errorCategoryList.push(Category1Types.PACKAGING_MATERIALS);
    }
    if (errorObj.hasOwnProperty(Category1TypeFields.OTHERS)) {
      errorCategoryList.push(Category1Types.OTHERS);
    }
    return errorCategoryList;
  };
  const errorList = useMemo(
    () => checkForError(methods.formState.errors),
    [methods.formState.errors]
  );

  useEffect(() => {
    const errorCategoryList = checkForError(methods.formState.errors);
    if (errorCategoryList.length > 0) {
      Notification({
        message: `There are some error in the ${errorCategoryList.join(', ')}`,
        description: '',
        type: NotificationTypes.ERROR,
      });
    }
  }, [errorList]);

  return (
    <Spin spinning={loading}>
      <div className={styles.categoryOneContainer}>
        <FormProvider {...methods}>
          <ScopeThreeHeader
            icon={<CategoryOne />}
            title="Category 1 - Purchased Goods And Services"
            year={useYearLabel(activeTab)}
            onSubmit={methods.handleSubmit(onSubmit)}
          />
          <div className={styles.categoryOneSectionsContainer}>
            <SharedAccordion
              accordion={true}
              expandIconPosition="end"
              activeKey={activeTabKey}
              onChange={handleAccordionChange}
              panels={PanelComponent({
                fields,
                append,
                appendPackagingMaterial,
                appendOtherMaterial,
                remove,
                packagingMaterialFields,
                removePackagingMaterial,
                otherMaterialFields,
                removeOtherMaterial,
                rawMaterialIngredientsMeta,
                rawMateialPackagingMeta,
                othersMeta,
                setActiveTabKey,
                errorList,
              })}
            />
          </div>
        </FormProvider>
      </div>
    </Spin>
  );
};

export default CategoryOneComponent;
